import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Trash2, Edit2, Check, Sparkles, Settings, Tag, Key, Download, Shield, Loader2, User, Scale } from "lucide-react";
import IagoAvatar from "../iago/IagoAvatar";
import clsx from "clsx";
import { useStore } from "../../stores/store";
import { CATEGORY_COLOR_PALETTE, categoryBg, categoryBorder, downloadFile } from "../../lib/utils";
import type { Category, Lane } from "../../types";
import { confirmAsync, promptAsync } from "../../stores/dialogStore";
import { api } from "../../lib/api";
import { supabase } from "../../lib/supabase";

export function SettingsModal() {
  const open = useStore((s) => s.settingsOpen);
  const setSettingsOpen = useStore((s) => s.setSettingsOpen);
  const categories = useStore((s) => s.categories);
  const cards = useStore((s) => s.cards);
  const createCategory = useStore((s) => s.createCategory);
  const updateCategoryItem = useStore((s) => s.updateCategoryItem);
  const deleteCategoryItem = useStore((s) => s.deleteCategoryItem);
  const userEmail = useStore((s) => s.userEmail);
  const userSettings = useStore((s) => s.userSettings);
  const setOnboardingOpen = useStore((s) => s.setOnboardingOpen);
  const removeGroqKey = useStore((s) => s.removeGroqKey);
  const saveNickname = useStore((s) => s.saveNickname);
  const saveWeightLaneIds = useStore((s) => s.saveWeightLaneIds);
  const lanes = useStore((s) => s.lanes);

  const [nicknameInput, setNicknameInput] = useState(userSettings.nickname || "");
  const [nicknameSaved, setNicknameSaved] = useState<"idle" | "saving" | "saved">("idle");
  const nicknameTimer = useRef<number | null>(null);

  useEffect(() => {
    setNicknameInput(userSettings.nickname || "");
  }, [userSettings.nickname]);

  function onNicknameChange(v: string) {
    setNicknameInput(v);
    if (nicknameTimer.current) window.clearTimeout(nicknameTimer.current);
    nicknameTimer.current = window.setTimeout(async () => {
      const next = v.trim().slice(0, 40);
      if (next === (userSettings.nickname || "")) return;
      setNicknameSaved("saving");
      try {
        await saveNickname(next);
        setNicknameSaved("saved");
        window.setTimeout(() => setNicknameSaved("idle"), 1200);
      } catch {
        setNicknameSaved("idle");
      }
    }, 600);
  }

  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState<string>(CATEGORY_COLOR_PALETTE[0]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Partial<Category>>({});
  const [err, setErr] = useState<string | null>(null);

  async function doCreate() {
    if (!newName.trim()) return;
    try {
      await createCategory({ name: newName.trim(), color: newColor });
      setNewName("");
      setErr(null);
    } catch (e: any) {
      setErr(e.message);
    }
  }

  async function saveEdit() {
    if (editingId == null) return;
    await updateCategoryItem(editingId, draft);
    setEditingId(null);
    setDraft({});
  }

  function startEdit(cat: Category) {
    setEditingId(cat.id);
    setDraft({ name: cat.name, color: cat.color, aiProposed: cat.aiProposed });
  }

  function countCardsWith(id: number) {
    return cards.filter((c) => (c.categoryIds || []).includes(id)).length;
  }

  const [exporting, setExporting] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  async function doExport() {
    setExporting(true);
    try {
      const data = await api.exportMyData();
      const date = new Date().toISOString().slice(0, 10);
      downloadFile(
        `onboard-meus-dados-${date}.json`,
        JSON.stringify(data, null, 2),
        "application/json",
      );
    } catch (e: any) {
      await confirmAsync({
        title: "Erro ao exportar",
        description: e.message,
        confirmLabel: "OK",
        cancelLabel: "",
      });
    } finally {
      setExporting(false);
    }
  }

  async function doDeleteAccount() {
    const confirm1 = await confirmAsync({
      title: "Excluir conta?",
      description:
        "Isso apaga PERMANENTEMENTE todas as suas tarefas, raias, categorias, comentários, chat e histórico. Não dá pra recuperar.",
      confirmLabel: "Continuar",
      cancelLabel: "Cancelar",
      danger: true,
    });
    if (!confirm1) return;

    const typed = await promptAsync({
      title: "Confirmação final",
      description: "Digite EXCLUIR (em maiúsculo) para confirmar.",
      placeholder: "EXCLUIR",
      confirmLabel: "Apagar tudo",
      validate: (v) => (v === "EXCLUIR" ? null : "digite EXCLUIR exatamente"),
    });
    if (typed !== "EXCLUIR") return;

    setDeletingAccount(true);
    try {
      await api.deleteMyAccount();
      await supabase.auth.signOut();
    } catch (e: any) {
      await confirmAsync({
        title: "Erro ao excluir",
        description: e.message,
        confirmLabel: "OK",
        cancelLabel: "",
      });
      setDeletingAccount(false);
    }
  }

  async function doDelete(cat: Category) {
    const n = countCardsWith(cat.id);
    const ok = await confirmAsync({
      title: `Remover "${cat.name}"?`,
      description: n === 0
        ? "Nenhum card usa essa categoria."
        : `${n} card${n === 1 ? "" : "s"} perder${n === 1 ? "á" : "ão"} essa categoria.`,
      confirmLabel: "Remover",
      danger: true,
    });
    if (!ok) return;
    await deleteCategoryItem(cat.id);
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center md:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
        >
          <div className="absolute inset-0 bg-black/65 backdrop-blur-md" onClick={() => setSettingsOpen(false)} />
          <motion.div
            className="glass relative flex h-full w-full flex-col overflow-hidden rounded-none shadow-2xl md:h-[85vh] md:w-[720px] md:max-w-[95vw] md:rounded-2xl"
            initial={{ scale: 0.96, y: 12, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.97, y: 6, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 280 }}
          >
            <div className="flex items-center justify-between border-b border-white/5 px-6 py-4">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/20 ring-1 ring-violet-400/30">
                  <Settings className="h-4 w-4 text-violet-300" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-100">Configurações</h2>
                  <p className="text-[11px] text-slate-500">Categorias do board</p>
                </div>
              </div>
              <button
                onClick={() => setSettingsOpen(false)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-white/5 hover:text-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-5">
              <section className="mb-6">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  <Key className="mr-1 inline h-3 w-3" /> Conta & API
                </h3>
                <div className="space-y-3 rounded-xl border border-white/5 bg-white/[0.02] p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Email</span>
                    <span className="font-mono text-xs text-slate-200">{userEmail || "..."}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-slate-400">Chave Groq</span>
                    {userSettings.hasGroqKey ? (
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-0.5 font-mono text-[11px] text-emerald-300 ring-1 ring-emerald-500/30">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_currentColor]" />
                          conectada · ···{userSettings.groqLast4}
                        </span>
                        <button
                          onClick={() => setOnboardingOpen(true)}
                          className="rounded px-2 py-0.5 text-xs text-slate-300 hover:bg-white/5"
                        >
                          atualizar
                        </button>
                        <button
                          onClick={async () => {
                            const ok = await confirmAsync({
                              title: "Remover chave Groq?",
                              description: "Você não conseguirá usar voz/chat/refine até configurar uma nova.",
                              confirmLabel: "Remover",
                              danger: true,
                            });
                            if (ok) await removeGroqKey();
                          }}
                          className="rounded px-2 py-0.5 text-xs text-rose-300 hover:bg-rose-500/10"
                        >
                          remover
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setOnboardingOpen(true)}
                        className="rounded-lg bg-violet-500/20 px-3 py-1 text-xs font-medium text-violet-200 ring-1 ring-violet-400/30 hover:bg-violet-500/30"
                      >
                        ⚠️ Configurar agora
                      </button>
                    )}
                  </div>
                </div>
              </section>

              <section className="mb-6">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  <User className="mr-1 inline h-3 w-3" /> Sobre você
                </h3>
                <div className="rounded-xl border border-cyan-400/20 bg-cyan-500/[0.04] p-4">
                  <div className="flex items-start gap-3">
                    <IagoAvatar size={36} />
                    <div className="flex-1">
                      <label className="mb-1 block text-xs font-medium text-slate-200">
                        Como o IAGO pode te chamar?
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          value={nicknameInput}
                          onChange={(e) => onNicknameChange(e.target.value)}
                          placeholder="ex: Maria, Dr. Silva, ou deixe em branco"
                          maxLength={40}
                          className="flex-1 rounded-md border border-white/10 bg-black/30 px-2 py-1.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-500/60 focus:outline-none"
                        />
                        <span className="min-w-[60px] text-right text-[10px] text-slate-500">
                          {nicknameSaved === "saving" && "salvando…"}
                          {nicknameSaved === "saved" && (
                            <span className="text-emerald-300">salvo</span>
                          )}
                        </span>
                      </div>
                      <p className="mt-2 text-[11px] text-slate-500">
                        usado com parcimônia no chat. em branco = só "você", sem vocativo.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-6">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  <Scale className="mr-1 inline h-3 w-3" /> Indicador PESO
                </h3>
                <WeightLanesSelector
                  lanes={lanes}
                  selectedIds={userSettings.weightLaneIds}
                  onSave={saveWeightLaneIds}
                />
              </section>

              <section className="mb-6">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  <Shield className="mr-1 inline h-3 w-3" /> Privacidade & seus dados (LGPD)
                </h3>
                <div className="space-y-2 rounded-xl border border-white/5 bg-white/[0.02] p-4 text-sm">
                  <p className="mb-3 text-[12px] leading-relaxed text-slate-400">
                    Conforme a <strong className="text-slate-200">Lei Geral de Proteção de Dados</strong>, você tem direito a exportar ou apagar tudo que é seu.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={doExport}
                      disabled={exporting}
                      className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-1.5 text-xs text-slate-200 transition hover:border-violet-400/40 hover:bg-white/5 disabled:opacity-50"
                    >
                      {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                      Baixar meus dados (JSON)
                    </button>
                    <button
                      onClick={doDeleteAccount}
                      disabled={deletingAccount}
                      className="flex items-center gap-1.5 rounded-lg border border-rose-500/30 bg-rose-500/5 px-3 py-1.5 text-xs text-rose-300 transition hover:border-rose-400/50 hover:bg-rose-500/10 disabled:opacity-50"
                    >
                      {deletingAccount ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      Excluir minha conta
                    </button>
                    <a
                      href="/privacy.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto text-[11px] text-violet-300 hover:text-violet-200 underline decoration-violet-400/30 underline-offset-2"
                    >
                      Política de privacidade
                    </a>
                  </div>
                </div>
              </section>

              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  <Tag className="mr-1 inline h-3 w-3" /> Nova categoria
                </h3>
                <p className="mb-3 text-[11px] text-slate-500">
                  Use categorias para organizar por contexto (empresas, projetos, vida pessoal, estudos, etc).
                  A IA só escolhe das categorias cadastradas aqui. Nunca inventa automaticamente.
                </p>
                <div className="flex flex-wrap items-end gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-3">
                  <div className="flex-1 min-w-[220px]">
                    <label className="mb-1 block text-[10px] uppercase text-slate-500">Nome</label>
                    <input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && doCreate()}
                      placeholder="ex: Empresa X, Projeto Z, Casa, Curso de Rust…"
                      className="w-full rounded-md border border-white/10 bg-black/30 px-2 py-1.5 text-sm focus:border-violet-500/60 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[10px] uppercase text-slate-500">Cor</label>
                    <div className="flex flex-wrap gap-1">
                      {CATEGORY_COLOR_PALETTE.map((c) => (
                        <button
                          key={c}
                          onClick={() => setNewColor(c)}
                          className={clsx(
                            "h-5 w-5 rounded-full border-2 transition",
                            newColor === c ? "border-white scale-110" : "border-transparent",
                          )}
                          style={{ backgroundColor: c }}
                          aria-label={c}
                        />
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={doCreate}
                    disabled={!newName.trim()}
                    className="flex items-center gap-1 rounded-md bg-violet-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-violet-400 disabled:opacity-40"
                  >
                    <Plus className="h-3.5 w-3.5" /> Criar
                  </button>
                </div>
                {err && <p className="mt-2 text-xs text-rose-300">{err}</p>}
              </section>

              <section className="mt-6">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Categorias cadastradas <span className="ml-1 font-mono text-slate-500">({categories.length})</span>
                  </h3>
                </div>
                {categories.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-sm text-slate-500">
                    Sem categorias ainda. Crie a primeira aí em cima.
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {categories.map((cat) => {
                      const editing = editingId === cat.id;
                      const n = countCardsWith(cat.id);
                      return (
                        <li
                          key={cat.id}
                          className="group flex items-center gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5"
                        >
                          {editing ? (
                            <>
                              <input
                                type="color"
                                value={draft.color || cat.color}
                                onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                                className="h-6 w-8 cursor-pointer rounded border-0 bg-transparent p-0"
                              />
                              <input
                                value={draft.name || ""}
                                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                className="flex-1 rounded border border-white/10 bg-black/30 px-2 py-1 text-sm"
                              />
                              {cat.aiProposed && (
                                <button
                                  onClick={() => setDraft({ ...draft, aiProposed: false })}
                                  className="rounded px-2 py-1 text-[10px] text-violet-300 hover:bg-white/5"
                                  title="Confirmar (remover flag IA)"
                                >
                                  confirmar
                                </button>
                              )}
                              <button
                                onClick={saveEdit}
                                className="rounded p-1 text-emerald-300 hover:bg-white/5"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingId(null);
                                  setDraft({});
                                }}
                                className="rounded p-1 text-slate-400 hover:bg-white/5"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <span
                                className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs"
                                style={{
                                  backgroundColor: categoryBg(cat.color),
                                  borderColor: categoryBorder(cat.color),
                                  color: "#fff",
                                }}
                              >
                                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: cat.color }} />
                                {cat.name}
                              </span>
                              {cat.aiProposed && (
                                <span className="inline-flex items-center gap-1 rounded-md bg-violet-500/15 px-1.5 py-0.5 text-[10px] text-violet-200" title="Proposta pela IA">
                                  <Sparkles className="h-2.5 w-2.5" /> proposta IA
                                </span>
                              )}
                              <span className="ml-auto text-[11px] text-slate-500">
                                {n} card{n === 1 ? "" : "s"}
                              </span>
                              <button
                                onClick={() => startEdit(cat)}
                                className="rounded p-1 text-slate-400 opacity-0 transition hover:bg-white/5 hover:text-slate-100 group-hover:opacity-100"
                              >
                                <Edit2 className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => doDelete(cat)}
                                className="rounded p-1 text-slate-400 opacity-0 transition hover:bg-rose-500/10 hover:text-rose-300 group-hover:opacity-100"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function WeightLanesSelector({
  lanes,
  selectedIds,
  onSave,
}: {
  lanes: Lane[];
  selectedIds: number[];
  onSave: (ids: number[]) => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const sorted = [...lanes].sort((a, b) => a.position - b.position);

  async function toggle(id: number) {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    setSaving(true);
    try {
      await onSave(next);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1200);
    } finally {
      setSaving(false);
    }
  }

  const usingDefault = selectedIds.length === 0;
  const hasHoje = sorted.some((l) => l.title.trim().toLowerCase() === "hoje");

  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
      <div className="mb-3 flex items-start justify-between gap-2">
        <p className="text-[12px] leading-relaxed text-slate-400">
          Escolha uma ou mais raias para somar o esforço (em story points) no indicador
          <span className="mx-1 rounded bg-violet-500/10 px-1.5 py-0.5 font-mono text-[10px] text-violet-200">PESO</span>
          do topo. Útil para saber a carga do dia de trabalho.
        </p>
        <span className="min-w-[56px] text-right text-[10px] text-slate-500">
          {saving && "salvando…"}
          {saved && !saving && <span className="text-emerald-300">salvo</span>}
        </span>
      </div>

      {sorted.length === 0 ? (
        <p className="text-xs text-slate-500">Nenhuma raia criada ainda.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {sorted.map((l) => {
            const active = selectedIds.includes(l.id);
            return (
              <button
                key={l.id}
                onClick={() => toggle(l.id)}
                disabled={saving}
                className={clsx(
                  "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] transition disabled:opacity-50",
                  active
                    ? "border-violet-400/50 bg-violet-500/15 text-violet-100"
                    : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/20 hover:text-slate-100",
                )}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: l.color }}
                />
                {l.title}
                {active && <Check className="h-3 w-3" />}
              </button>
            );
          })}
        </div>
      )}

      <p className="mt-3 text-[11px] text-slate-500">
        {usingDefault
          ? hasHoje
            ? "Sem seleção: estamos usando a raia \"Hoje\" por padrão."
            : "Sem seleção e sem raia \"Hoje\": estamos usando a primeira raia não concluída."
          : `Contando ${selectedIds.length} raia${selectedIds.length === 1 ? "" : "s"} selecionada${selectedIds.length === 1 ? "" : "s"}.`}
      </p>
    </div>
  );
}
